import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export type AzureConnectionStatus = 'PENDING_VERIFICATION' | 'ACTIVE' | 'FAILED';

/** One connected Azure subscription (service-principal secrets stripped). */
export interface AzureSubscription {
  subscriptionId:    string;
  label?:            string;
  defaultLocation?:  string;
  enabledLocations:  string[];
  isPrimary?:        boolean;
  connectionStatus?: AzureConnectionStatus;
  healthStatus?:     string;
  lastTestedAt?:     string;
  lastSyncAt?:       string;
  failureReason?:    string;
  connectedAt?:      string;
}

export interface AzureConnection {
  provider:                   'AZURE';
  connectionStatus:           AzureConnectionStatus;
  servicePrincipalConfigured: boolean;
  tenantId:                   string | null;
  clientId:                   string | null;
  subscriptions:              AzureSubscription[];
  primarySubscriptionId:      string | null;
  subscriptionCount:          number;
}

export interface AzureTestResult {
  success:          boolean;
  subscriptionId:   string;
  connectionStatus: AzureConnectionStatus;
  verificationMode: 'stub' | 'live';
}

@Injectable({ providedIn: 'root' })
export class AzureService {
  private readonly base = `${environment.apiUrl}/api/v1/integrations/azure`;

  constructor(private readonly http: HttpClient) {}

  /** The Azure integration state (subscriptions + service-principal status). */
  getConnection(): Observable<AzureConnection | null> {
    return this.http.get<AzureConnection>(`${this.base}/connection`).pipe(
      catchError(err => {
        if (err.status === 404) return of(null);
        throw err;
      }),
    );
  }

  /** Connects (and validates) an Azure subscription via a service principal. */
  testConnection(input: {
    tenantId: string; clientId: string; clientSecret: string; subscriptionId: string;
    displayName?: string; defaultLocation?: string; enabledLocations?: string[];
  }): Observable<AzureTestResult> {
    return this.http.post<AzureTestResult>(`${this.base}/test-connection`, input);
  }

  listSubscriptions(): Observable<{ subscriptions: AzureSubscription[]; primarySubscriptionId?: string; subscriptionCount: number }> {
    return this.http.get<{ subscriptions: AzureSubscription[]; primarySubscriptionId?: string; subscriptionCount: number }>(`${this.base}/subscriptions`);
  }

  deleteSubscription(subscriptionId: string): Observable<{ message: string; subscriptionId: string }> {
    return this.http.delete<{ message: string; subscriptionId: string }>(`${this.base}/subscriptions/${subscriptionId}`);
  }

  updateSubscriptionLocations(subscriptionId: string, defaultLocation: string, enabledLocations: string[]): Observable<{ subscriptionId: string; defaultLocation: string; enabledLocations: string[] }> {
    return this.http.patch<{ subscriptionId: string; defaultLocation: string; enabledLocations: string[] }>(
      `${this.base}/subscriptions/${subscriptionId}/locations`, { defaultLocation, enabledLocations },
    );
  }

  /** Re-verifies a connected subscription using the stored service principal (no secret re-entry). */
  verifySubscription(subscriptionId: string): Observable<{ success: boolean; subscriptionId: string; connectionStatus: AzureConnectionStatus; message?: string }> {
    return this.http.post<{ success: boolean; subscriptionId: string; connectionStatus: AzureConnectionStatus; message?: string }>(
      `${this.base}/subscriptions/${subscriptionId}/verify`, {},
    );
  }

  /** Promotes an existing ACTIVE subscription to be the primary. */
  setPrimarySubscription(subscriptionId: string): Observable<{ subscriptionId: string; primarySubscriptionId: string; changed: boolean }> {
    return this.http.post<{ subscriptionId: string; primarySubscriptionId: string; changed: boolean }>(
      `${this.base}/subscriptions/${subscriptionId}/primary`, {},
    );
  }

  disconnect(): Observable<{ message: string; subscriptionsRemoved: number }> {
    return this.http.delete<{ message: string; subscriptionsRemoved: number }>(`${this.base}/connection`);
  }
}
